import styled, { css } from 'styled-components';
import BigNumber from 'bignumber.js';
import { CellContext, Column } from '@tanstack/react-table';
import { useSelector } from 'react-redux';
import { ReactNode, useMemo } from 'react';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';

import { useRowIndex } from '@paradigm/design-system/src/components/Table';
import {
  BaseLeaderboardEntity,
  formatBigNumber,
} from '@paradigm/features/src/leaderboard/entities/shared';
import { formatOrdinal } from '@paradigm/utils/formatDecimal';
import { selectIsFirmDisabledLeaderboardAnonymity } from '@paradigm/store/auth';
import AnonEyeIcon from '@paradigm/design-system/src/assets/AnonEyeIcon';
import EyeIcon from '@paradigm/design-system/src/assets/EyeIcon';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import Tooltip from '@paradigm/design-system/src/components/Tooltip';
import RankBadge from './RankBadge';

interface RankCellProps {
  isUserRow?: boolean;
}

export function RankCell({ isUserRow }: RankCellProps) {
  const currentRowIndex = useRowIndex();
  const rank = currentRowIndex + 1;
  return <RankBadge data-testid="rank" rank={rank} isUserRow={isUserRow} />;
}

export function PercentageCell<T>({ cell }: CellContext<T, T>) {
  return (
    <Cell
      data-testid={cell.column.id}
      highlight={cell.column.getIsSorted() !== false}
    >
      {cell.getValue<BigNumber>().toString()}%
    </Cell>
  );
}

export function BigNumberCell<T>({ cell }: CellContext<T, T>) {
  const bigNumber = cell.getValue<BigNumber>();
  const value = bigNumber.isNaN() ? NEGATIVE_SIGN : formatBigNumber(bigNumber);
  return (
    <Cell
      data-testid={cell.column.id}
      highlight={cell.column.getIsSorted() !== false}
    >
      {value}
    </Cell>
  );
}

export function OrdinalCell<T>({ cell }: CellContext<T, T>) {
  return (
    <Cell
      data-testid={cell.column.id}
      highlight={cell.column.getIsSorted() !== false}
    >
      {formatOrdinal(cell.getValue<BigNumber>().toString())}
    </Cell>
  );
}

interface HeaderCellProps<T> {
  readonly column: Column<T, T>;
  readonly title: string;
  readonly tooltipContent?: ReactNode;
}

export function HeaderCell<T>({
  column,
  title,
  tooltipContent,
}: HeaderCellProps<T>) {
  const sortedValue = column.getIsSorted();
  const isSorted = sortedValue !== false;
  const dataTestId = isSorted ? `${column.id}-${sortedValue}` : column.id;

  return (
    <Cell data-testid={dataTestId} opacity={0.5} highlight={isSorted}>
      {title}
      {tooltipContent != null && (
        <Tooltip
          id={`${column.id}-column-formula`}
          tooltipContent={tooltipContent}
        />
      )}
    </Cell>
  );
}

const EYE_ICON_CSS = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: -20px;
`;

export function FirmCell<T extends BaseLeaderboardEntity>({
  cell,
  isLiquidityTab = false,
}: CellContext<T, T> & { isLiquidityTab?: boolean }) {
  const { isFromUser } = cell.row.original;
  const isFirmDisabledLeaderboardAnonymity = useSelector(
    selectIsFirmDisabledLeaderboardAnonymity,
  );

  const userAnonIcon = useMemo(() => {
    if (!isFromUser) return null;
    const anonIcon = (
      <AnonEyeIcon css={EYE_ICON_CSS} data-testid="anonymous-firm" />
    );
    const publicIcon = <EyeIcon css={EYE_ICON_CSS} data-testid="public-firm" />;

    /** Liquidity tabs are always anon */
    if (isLiquidityTab) return anonIcon;

    return isFirmDisabledLeaderboardAnonymity ? publicIcon : anonIcon;
  }, [isFirmDisabledLeaderboardAnonymity, isFromUser, isLiquidityTab]);

  return (
    <div css="position: relative;">
      {userAnonIcon}
      <Cell
        data-testid={cell.column.id}
        highlight={isFromUser}
        bold={isFromUser}
        ellipsis
      >
        {cell.getValue<string>()}
      </Cell>
    </div>
  );
}

export const Cell = styled.span<{
  highlight?: boolean;
  bold?: boolean;
  ellipsis?: boolean;
  opacity?: number;
}>(
  (props) => css`
    color: white;
    opacity: ${props.opacity ?? 0.7};
    text-align: left;
    font-weight: normal;
    display: flex;
    align-items: center;
    gap: 6px;
    ${props.bold === true && 'font-weight: 600;'}
    ${props.highlight === true &&
    css`
      opacity: 1;
      color: ${paradigmLightBlue};
    `}
    ${props.ellipsis === true &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}

    &:hover {
      opacity: 1;
    }
  `,
);

export function EmptyCell() {
  return <span />;
}
